import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';

import { getAdministraciones } from '../service/AdministracionService';
import { getLiquidacionMensual, saveMontoPorUF } from '../service/LiquidacionMensualService';
import { getMailTemplates, enviarConTemplate } from '../service/MailTemplateService';
import MailTemplateEditor from '../components/MailTemplateEditor';
import ReporteLiquidacionAdministracion from '../components/reportePDF/ReporteLiquidacionAdministracion';

const lbl = { display: 'block', fontSize: '0.8rem', color: '#666', marginBottom: 4 };

const fmtMoneda = (v) => `$ ${Number(v || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`;
const fmtPeriodo = (periodo) => periodo
    ? periodo.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' })
    : '';

const primerDiaMesActual = () => {
    const hoy = new Date();
    return new Date(hoy.getFullYear(), hoy.getMonth(), 1);
};

const LiquidacionMensualAdministradoras = () => {
    const toast = useRef(null);
    const pdfRef = useRef(null);
    const montoRefs = useRef({});

    const [administraciones, setAdministraciones] = useState([]);
    const [administracionId, setAdministracionId] = useState(null);
    const [periodo, setPeriodo] = useState(primerDiaMesActual());
    const [filas, setFilas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [buscado, setBuscado] = useState(false);

    const [pdfDialogVisible, setPdfDialogVisible] = useState(false);
    const [mailDialogVisible, setMailDialogVisible] = useState(false);
    const [cargandoPlantillaMail, setCargandoPlantillaMail] = useState(false);
    const [enviandoMail, setEnviandoMail] = useState(false);
    const [mailAsunto, setMailAsunto] = useState('');
    const [mailHtmlTemplate, setMailHtmlTemplate] = useState('');
    const [mailEsDefault, setMailEsDefault] = useState(false);

    const administracionSeleccionada = useMemo(
        () => administraciones.find((a) => a.administracionId === administracionId),
        [administraciones, administracionId]
    );

    const totalGeneral = useMemo(
        () => filas.reduce((acc, f) => acc + (f.totalBarrio || 0), 0),
        [filas]
    );

    const periodoStr = fmtPeriodo(periodo);

    // ─── Carga inicial de administradoras (solo no-demo) ───────────────────────
    useEffect(() => {
        getAdministraciones(true)
            .then(({ data }) => setAdministraciones(data))
            .catch(() => toast.current?.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar las administradoras', life: 3000 }));
    }, []);

    // ─── Buscar liquidación ─────────────────────────────────────────────────────
    const buscar = async () => {
        if (!administracionId) {
            toast.current?.show({ severity: 'warn', summary: 'Requerido', detail: 'Seleccione una administradora', life: 3000 });
            return;
        }
        if (!periodo) {
            toast.current?.show({ severity: 'warn', summary: 'Requerido', detail: 'Seleccione el período', life: 3000 });
            return;
        }
        setLoading(true);
        try {
            const { data } = await getLiquidacionMensual(administracionId, periodo.toISOString());
            setFilas(data);
            setBuscado(true);
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'No se pudo obtener la liquidación mensual', life: 3000 });
        } finally {
            setLoading(false);
        }
    };

    // ─── Edición de monto por UF (por barrio) ──────────────────────────────────
    const handleMontoChange = (barrioId, value) => {
        montoRefs.current[barrioId] = value;
        setFilas((prev) => prev.map((f) => f.barrioId === barrioId
            ? { ...f, montoPorUF: value, totalBarrio: (value || 0) * f.cantidadUF }
            : f));
    };

    const handleMontoBlur = async (row) => {
        const value = montoRefs.current[row.barrioId] ?? row.montoPorUF ?? 0;
        try {
            const { data } = await saveMontoPorUF({
                liquidacionBarrioId: row.liquidacionBarrioId,
                barrioId: row.barrioId,
                administracionId,
                periodo,
                montoPorUF: value,
            });
            setFilas((prev) => prev.map((f) => f.barrioId === row.barrioId
                ? { ...f, liquidacionBarrioId: data.liquidacionBarrioId }
                : f));
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'No se pudo guardar el monto por UF', life: 3000 });
        }
    };

    // ─── PDF: imprimir / guardar ────────────────────────────────────────────────
    const handlePrint = () => {
        const content = pdfRef.current;
        if (!content) return;

        const win = window.open('', '_blank', 'width=900,height=700');
        if (!win) {
            alert('Permita ventanas emergentes en su navegador para imprimir el PDF.');
            return;
        }

        win.document.write(
            '<!DOCTYPE html><html><head><meta charset="utf-8">' +
            '<title>Liquidacion Mensual</title>' +
            '<style>body{margin:0;padding:0;background:#fff;}</style>' +
            '</head><body>' +
            content.innerHTML +
            '</body></html>'
        );
        win.document.close();

        let done = false;
        const doPrint = () => {
            if (done) return;
            done = true;
            win.focus();
            win.print();
        };
        win.addEventListener('load', doPrint, { once: true });
        setTimeout(doPrint, 800);
    };

    // ─── Mail ────────────────────────────────────────────────────────────────────
    const handleAbrirDialogMail = async () => {
        if (!administracionSeleccionada?.email) {
            toast.current?.show({ severity: 'warn', summary: 'Sin email', detail: 'La administradora no tiene un email cargado', life: 3000 });
            return;
        }
        setCargandoPlantillaMail(true);
        try {
            const { data } = await getMailTemplates(administracionId, 0);
            const tpl = data.find((t) => t.tipoMail === 'LiquidacionAdministracion');
            setMailAsunto(tpl?.asunto || '');
            setMailHtmlTemplate(tpl?.htmlTemplate || '');
            setMailEsDefault(!!tpl?.esDefault);
            setMailDialogVisible(true);
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'No se pudo cargar la plantilla de mail', life: 3000 });
        } finally {
            setCargandoPlantillaMail(false);
        }
    };

    const handleConfirmarEnvioMail = async () => {
        setEnviandoMail(true);
        try {
            const htmlContent = pdfRef.current.outerHTML;

            const html2pdf = (await import('html2pdf.js')).default;
            const dataUri = await html2pdf()
                .set({
                    margin: 0,
                    filename: 'liquidacion-administradora.pdf',
                    html2canvas: { scale: 2, useCORS: true, allowTaint: true, logging: false },
                    jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
                })
                .from(htmlContent)
                .outputPdf('datauristring');

            const adjuntoBase64 = dataUri.split(',')[1];

            const { data } = await enviarConTemplate({
                administracionId,
                barrioId: 0,
                tipoMail: 'LiquidacionAdministracion',
                asunto: mailAsunto,
                htmlTemplate: mailHtmlTemplate,
                placeholdersComunes: {
                    administracion: administracionSeleccionada?.razonSocial || '',
                    periodo: periodoStr,
                    total: fmtMoneda(totalGeneral),
                },
                destinatarios: [{ email: administracionSeleccionada.email, placeholders: {} }],
                adjuntoBase64,
                adjuntoNombre: 'liquidacion-administradora.pdf',
            });

            setMailDialogVisible(false);
            toast.current?.show({ severity: 'success', summary: 'Mail enviado', detail: `Enviado correctamente a ${data.enviados} destinatario(s).`, life: 3000 });
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Error al enviar el mail: ' + err.message, life: 4000 });
        } finally {
            setEnviandoMail(false);
        }
    };

    // ─── Templates de columnas ───────────────────────────────────────────────────
    const montoBodyTemplate = (row) => (
        <InputNumber
            value={row.montoPorUF}
            onValueChange={(e) => handleMontoChange(row.barrioId, e.value)}
            onBlur={() => handleMontoBlur(row)}
            mode="currency" currency="ARS" locale="es-AR"
            className="p-inputtext-sm" style={{ width: '100%' }}
        />
    );

    const totalBodyTemplate = (row) => fmtMoneda(row.totalBarrio);

    // ─── Render ───────────────────────────────────────────────────────────────────
    return (
        <>
            <h4>Liquidación Mensual por Administradora</h4>
            <br />
            <Toast ref={toast} />

            {loading && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ProgressSpinner style={{ width: 52, height: 52 }} strokeWidth="4" />
                </div>
            )}

            {/* ─── Barra superior ──────────────────────────────────────────────── */}
            <div className="flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div className="flex gap-2 flex-wrap align-items-end">
                    <div>
                        <label style={lbl}>Administradora</label>
                        <Dropdown value={administracionId} options={administraciones}
                            optionLabel="razonSocial" optionValue="administracionId"
                            onChange={(e) => setAdministracionId(e.value)}
                            placeholder="Seleccionar administradora" filter
                            className="p-inputtext-sm" style={{ width: 260 }} />
                    </div>
                    <div>
                        <label style={lbl}>Período</label>
                        <Calendar value={periodo} onChange={(e) => setPeriodo(e.value)}
                            view="month" dateFormat="mm/yy" placeholder="mm/aaaa"
                            className="p-inputtext-sm" style={{ width: 140 }} />
                    </div>
                    <Button label="Buscar" icon="pi pi-search" onClick={buscar} className="p-button-sm" />
                </div>
                <div className="flex gap-2">
                    <Button label="Vista previa PDF" icon="pi pi-file-pdf" className="p-button-sm"
                        disabled={filas.length === 0}
                        onClick={() => setPdfDialogVisible(true)} />
                </div>
            </div>

            {/* ─── Grilla ──────────────────────────────────────────────────────── */}
            <div className="grid">
                <div className="col-12">
                    <DataTable value={filas} paginator rows={15} rowsPerPageOptions={[10, 15, 25, 50]}
                        showGridlines responsiveLayout="scroll"
                        emptyMessage={buscado ? 'La administradora no tiene barrios' : 'Seleccione una administradora y un período, y presione Buscar'}
                        className="p-datatable-gridlines bg-white">
                        <Column field="nombreBarrio" header="Barrio" sortable />
                        <Column field="cantidadUF" header="Cant. UF" sortable style={{ width: '8rem', textAlign: 'center' }} />
                        <Column field="montoPorUF" header="Monto por UF" body={montoBodyTemplate} style={{ width: '14rem' }} />
                        <Column field="totalBarrio" header="Total" body={totalBodyTemplate} sortable style={{ width: '10rem', textAlign: 'right' }} />
                    </DataTable>
                    {filas.length > 0 && (
                        <div className="flex justify-content-end mt-2 mr-2">
                            <span style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#1a3c5e' }}>
                                Total General: {fmtMoneda(totalGeneral)}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* ─── PDF off-screen (para imprimir / adjuntar al mail) ─────────────── */}
            <div style={{ position: 'absolute', left: '-9999px', top: 0, width: '210mm' }}>
                <div ref={pdfRef}>
                    <ReporteLiquidacionAdministracion
                        administracion={administracionSeleccionada?.razonSocial}
                        periodoStr={periodoStr}
                        filas={filas}
                        totalGeneral={totalGeneral}
                    />
                </div>
            </div>

            {/* ─── Dialog: Preview PDF ─────────────────────────────────────────── */}
            <Dialog
                header="Preview Liquidación Mensual"
                visible={pdfDialogVisible}
                style={{ width: '90vw', maxWidth: '900px' }}
                onHide={() => setPdfDialogVisible(false)}
                footer={
                    <div className="flex gap-2 justify-content-end">
                        <Button label="Imprimir / Guardar PDF" icon="pi pi-print" onClick={handlePrint} />
                        <Button label="Enviar por mail" icon="pi pi-envelope" loading={cargandoPlantillaMail} onClick={handleAbrirDialogMail} />
                        <Button label="Cerrar" icon="pi pi-times" className="p-button-text" onClick={() => setPdfDialogVisible(false)} />
                    </div>
                }
            >
                <div style={{ overflowY: 'auto', maxHeight: '75vh' }}>
                    <ReporteLiquidacionAdministracion
                        administracion={administracionSeleccionada?.razonSocial}
                        periodoStr={periodoStr}
                        filas={filas}
                        totalGeneral={totalGeneral}
                    />
                </div>
            </Dialog>

            {/* ─── Dialog: revisar/editar el mail antes de enviarlo ──────────────── */}
            <Dialog
                header="Enviar por mail"
                visible={mailDialogVisible}
                style={{ width: '90vw', maxWidth: '750px' }}
                breakpoints={{ '960px': '100vw' }}
                modal
                onHide={() => setMailDialogVisible(false)}
                footer={
                    <div className="flex gap-2 justify-content-end">
                        <Button label="Cancelar" icon="pi pi-times" className="p-button-text" onClick={() => setMailDialogVisible(false)} />
                        <Button label="Enviar correo" icon="pi pi-send" loading={enviandoMail} onClick={handleConfirmarEnvioMail} />
                    </div>
                }
            >
                <MailTemplateEditor
                    tipoMail="LiquidacionAdministracion"
                    asunto={mailAsunto}
                    onChangeAsunto={setMailAsunto}
                    htmlTemplate={mailHtmlTemplate}
                    onChangeHtml={setMailHtmlTemplate}
                    esDefault={mailEsDefault}
                    onError={(msg) => toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 })}
                />
            </Dialog>
        </>
    );
};

export default LiquidacionMensualAdministradoras;
